// Define the supported product view aspects
const SUPPORTED_VIEW_ASPECTS = ["S", "T", "W"];

// Define the supported product view rotations
const SUPPORTED_VIEW_ROTATIONS = [0, 90, 180, 270];

// Define the default configured role view settings
const DEFAULT_ROLE_VIEW_SETTINGS = {
    aspects: ["S", "T", "W"],
    rotations: [0, 90, 180, 270],
};



// Get the display priority used by the product preview carousel
const getProductPreviewViewPriority = (view) => {

    // Put the explicit reference view first
    if (String(view?.reference || "").trim().toUpperCase() === "Y") {
        return 0;
    }

    // Put default-style views after the reference view
    if (String(view?.style || "").trim().toLowerCase() === "default") {
        return 1;
    }

    // Put every other compatible view after those
    return 2;

};



const normaliseViewSettings = (viewSettings, fallbackViewSettings) => {

    // Use the fallback settings when the supplied value is not an object
    if (!viewSettings || typeof viewSettings !== "object") {
        return {
            aspects: [...fallbackViewSettings.aspects],
            rotations: [...fallbackViewSettings.rotations],
        };
    }

    // Start with empty aspect and rotation lists
    let aspects = [];
    let rotations = [];

    // Normalise supplied aspects
    if (Array.isArray(viewSettings.aspects)) {

        aspects = viewSettings.aspects
            .map((aspect) => String(aspect || "").trim().toUpperCase())
            .filter((aspect) => SUPPORTED_VIEW_ASPECTS.includes(aspect));

        // Remove duplicate aspects while preserving their order
        aspects = Array.from(new Set(aspects));

    } else {

        // Use the fallback aspects when the field is missing
        aspects = [...fallbackViewSettings.aspects];

    }

    // Normalise supplied rotations
    if (Array.isArray(viewSettings.rotations)) {

        rotations = viewSettings.rotations
            .map((rotation) => Number(rotation))
            .filter((rotation) => SUPPORTED_VIEW_ROTATIONS.includes(rotation));

        // Remove duplicate rotations while preserving their order
        rotations = Array.from(new Set(rotations));

    } else {

        // Use the fallback rotations when the field is missing
        rotations = [...fallbackViewSettings.rotations];

    }

    // Return the normalised settings
    return {
        aspects: aspects,
        rotations: rotations,
    };

};



export const normaliseRoleViewSettings = (viewSettings) => {

    // Configured roles without saved settings support every view by default
    return normaliseViewSettings(viewSettings, DEFAULT_ROLE_VIEW_SETTINGS);

};

// Get the supported design aspects for one real product view
export const normaliseProductViewAspects = (view) => {

    // Start with the new multi-aspect field
    let aspects = [];

    if (Array.isArray(view?.aspects)) {

        aspects = view.aspects
            .map((aspect) => String(aspect || "").trim().toUpperCase())
            .filter((aspect) => SUPPORTED_VIEW_ASPECTS.includes(aspect));

        // Remove duplicate aspects while preserving their order
        aspects = Array.from(new Set(aspects));

    }

    // Temporarily support API responses created before the aspects field was introduced
    if (aspects.length === 0) {

        const legacyAspect = String(view?.aspect || "").trim().toUpperCase();

        if (SUPPORTED_VIEW_ASPECTS.includes(legacyAspect)) { aspects.push(legacyAspect); }

    }

    // Return every aspect this product view can display
    return aspects;

};

// Get the first live product view allowed by one configured product role
export const getFirstConfiguredProductPreviewView = (canonicalProduct, configuredRole, roleCode) => {

    // A configured product role is required because it owns the available view settings
    if (!configuredRole) { return null; }

    // Accept either a canonical POD product or an already-expanded custom product
    const customProduct = canonicalProduct?.custom_product || canonicalProduct;

    // Read the requested print-area role
    const safeRoleCode = String(roleCode || configuredRole?.role || "").trim();

    // Stop when the role cannot be identified
    if (!safeRoleCode) { return null; }

    // Read the configured role's allowed aspects and rotations
    const roleViewSettings = normaliseRoleViewSettings(configuredRole?.view_settings);

    // Read product surfaces safely
    const surfaces = Array.isArray(customProduct?.surfaces) ? customProduct.surfaces : [];

    // Store every live view available to this configured product role
    const availableViews = [];

    // Check surfaces in their existing product order
    surfaces.forEach((surface) => {

        // Read print areas safely
        const printAreas = Array.isArray(surface?.print_areas) ? surface.print_areas : [];

        // Check print areas in their existing product order
        printAreas.forEach((printArea) => {

            // Only use the print area belonging to the configured role
            if (String(printArea?.role || "").trim() !== safeRoleCode) { return; }

            // Read the real product views for this print area
            const views = Array.isArray(printArea?.views) ? printArea.views : [];

            // Keep only views that this configured role can actually use
            views.forEach((view) => {

                // Skip inactive views
                if (String(view?.active || "Y").trim().toUpperCase() === "N") { return; }

                // A live product preview requires a preview asset
                const assets = Array.isArray(view?.assets) ? view.assets : [];

                // Check whether this view can be rendered as a live preview
                const hasPreviewAsset = assets.some((asset) => {
                    return String(asset?.type || "").trim().toLowerCase() === "preview";
                });

                // Skip views that cannot render as live product previews
                if (!hasPreviewAsset) { return; }

                // Read every design aspect this real product view can display
                const viewAspects = normaliseProductViewAspects(view);

                // Read rotation from either supported field name
                let rotationValue = view?.rotation;

                if (rotationValue === null || rotationValue === undefined || rotationValue === "") {
                    rotationValue = view?.rotate;
                }

                // Convert the rotation to a number
                const rotation = Number(rotationValue);

                // Skip malformed view data
                if (viewAspects.length === 0) { return; }
                if (!SUPPORTED_VIEW_ROTATIONS.includes(rotation)) { return; }

                // Check whether the real view and configured role share at least one aspect
                const hasCompatibleAspect = roleViewSettings.aspects.some((aspect) => viewAspects.includes(aspect));

                // Skip views excluded by the configured product role
                if (!hasCompatibleAspect) { return; }
                if (!roleViewSettings.rotations.includes(rotation)) { return; }

                // Store the exact view plus the context required by the preview module
                availableViews.push({
                    ...view,
                    surface_code: view?.surface_code || surface?.code || "default",
                    print_area_code: view?.print_area_code || printArea?.code || "default",
                    role: view?.role || printArea?.role || safeRoleCode,
                });

            });

        });

    });

    // Match the same reference/default ordering used by the product image carousel
    availableViews.sort((firstView, secondView) => {
        return getProductPreviewViewPriority(firstView) - getProductPreviewViewPriority(secondView);
    });

    // Return the first configured design-product view
    return availableViews[0] || null;

};



export const normaliseMockupViewSettings = (viewSettings) => {

    // Mockups without view settings are not compatible with any configured role
    return normaliseViewSettings(viewSettings, {
        aspects: [],
        rotations: [],
    });

};

export const getCanonicalProductMockups = (canonicalProduct) => {

    // Accept either a canonical POD product or an already-expanded custom product
    const customProduct = canonicalProduct?.custom_product || canonicalProduct;

    // Read the mockup ids selected for this canonical POD product
    const configuredMockupIds = Array.isArray(canonicalProduct?.custom_product_config?.mockups)
        ? new Set(
            canonicalProduct.custom_product_config.mockups
                .map((mockupId) => Number(mockupId))
                .filter((mockupId) => mockupId)
        )
        : null;

    // Store unique mockups in their canonical order
    const mockups = [];
    const addedMockupIds = new Set();

    // Add a canonical mockup once
    const addMockup = (mockup) => {

        // Read the mockup id from either supported response shape
        const mockupId = Number(mockup?.id || mockup?.cp_mockups_id);

        // Skip invalid or duplicate mockups
        if (!mockupId || addedMockupIds.has(mockupId)) { return; }

        // Respect the canonical POD product's selected mockup ids
        if (configuredMockupIds?.size > 0 && !configuredMockupIds.has(mockupId)) { return; }

        // Store the mockup
        addedMockupIds.add(mockupId);
        mockups.push(mockup);

    };

    // The canonical POD product response supplies its selected mockups here
    const canonicalMockups = Array.isArray(customProduct?.mockups)
        ? customProduct.mockups
        : [];

    canonicalMockups.forEach((mockup) => addMockup(mockup));

    // Return immediately when the canonical response supplied its mockups
    if (mockups.length > 0) { return mockups; }

    // Retain support for older responses which nested mockups under print areas
    const surfaces = Array.isArray(customProduct?.surfaces)
        ? customProduct.surfaces
        : [];

    surfaces.forEach((surface) => {

        // Read print areas safely
        const printAreas = Array.isArray(surface?.print_areas)
            ? surface.print_areas
            : [];

        printAreas.forEach((printArea) => {

            // Add mockups from the legacy nested response shape
            const locationMockups = Array.isArray(printArea?.mockups)
                ? printArea.mockups
                : [];

            locationMockups.forEach((mockup) => addMockup(mockup));

        });

    });

    // Return the canonical product's available mockups
    return mockups;

};


export const getMockupConfigurationKey = (mockupId, aspect, rotation) => {

    // Normalise the exact configuration values
    const safeMockupId = String(mockupId || "");
    const safeAspect = String(aspect || "").trim().toUpperCase();
    const hasRotation = rotation !== null && rotation !== undefined && rotation !== "";
    const safeRotation = Number(rotation);

    // Return an empty key when any value is invalid
    if (!safeMockupId || !SUPPORTED_VIEW_ASPECTS.includes(safeAspect) || !hasRotation || !SUPPORTED_VIEW_ROTATIONS.includes(safeRotation)) {
        return "";
    }

    // Return a stable key for this exact generated image configuration
    return [safeMockupId, safeAspect, safeRotation].join("|");

};

// Count the unique locations one mockup uses for this custom product
const getMockupProductLocationCount = (canonicalProduct, mockup) => {

    // Accept either a canonical POD product or an expanded custom product
    let customProduct = canonicalProduct;

    if (canonicalProduct?.custom_product) {
        customProduct = canonicalProduct.custom_product;
    }

    // Read the exact custom product code
    let customProductCode = "";

    if (customProduct?.product_code) {
        customProductCode = String(customProduct.product_code);
    } else if (customProduct?.code) {
        customProductCode = String(customProduct.code);
    }

    // Read mockup locations safely
    let mockupLocations = [];

    if (Array.isArray(mockup?.locations)) {
        mockupLocations = mockup.locations;
    }

    // Store each exact location once
    const locationKeys = new Set();

    mockupLocations.forEach((location) => {

        // Ignore locations belonging to a different custom product
        if (
            customProductCode &&
            String(location?.product_code || "") !== customProductCode
        ) {
            return;
        }

        // Read the exact product location
        const surfaceCode = String(location?.surface_code || "");
        const printAreaCode = String(location?.print_area_code || "");

        // Ignore incomplete location rows
        if (!surfaceCode || !printAreaCode) { return; }

        // Store the unique location
        locationKeys.add(surfaceCode + "|" + printAreaCode);

    });

    // Return the number of unique locations used for this product
    return locationKeys.size;

};

// Get every aspect and rotation combination supported by an active product view
const getSupportedProductViewConfigurationKeys = (
    canonicalProduct,
    configuredRoles
) => {

    // Start with the supplied canonical product
    let customProduct = canonicalProduct;

    // Use the expanded custom product when available
    if (canonicalProduct?.custom_product) {
        customProduct = canonicalProduct.custom_product;
    }

    // Read the actual product surfaces safely
    let surfaces = [];

    if (Array.isArray(customProduct?.surfaces)) {
        surfaces = customProduct.surfaces;
    }

    // Build a lookup of active configured roles and their allowed view settings
    const configuredRoleMap = {};

    if (Array.isArray(configuredRoles)) {

        configuredRoles.forEach((configuredRole) => {

            // Read the role code
            const roleCode =
                String(configuredRole?.role || "");

            // Ignore missing roles
            if (!roleCode) {
                return;
            }

            // Ignore inactive roles
            if (
                String(
                    configuredRole?.active || "Y"
                ).toUpperCase() === "N"
            ) {
                return;
            }

            // Store this role's permitted aspects and rotations
            configuredRoleMap[roleCode] =
                normaliseRoleViewSettings(
                    configuredRole?.view_settings
                );

        });

    }

    // Store each supported aspect and rotation combination once
    const supportedConfigurationKeys = new Set();

    surfaces.forEach((surface) => {

        // Read the print areas safely
        let printAreas = [];

        if (Array.isArray(surface?.print_areas)) {
            printAreas = surface.print_areas;
        }

        printAreas.forEach((printArea) => {

            // Read the role belonging to this print area
            const roleCode =
                String(printArea?.role || "");

            // Find the active configured role settings
            const roleViewSettings =
                configuredRoleMap[roleCode];

            // Ignore print areas without an active configured role
            if (!roleViewSettings) {
                return;
            }

            // Read the real product views safely
            let views = [];

            if (Array.isArray(printArea?.views)) {
                views = printArea.views;
            }

            views.forEach((view) => {

                // Ignore inactive product views
                if (
                    String(
                        view?.active || "Y"
                    ).toUpperCase() === "N"
                ) {
                    return;
                }

                // Read every design aspect this real product view can display
                const viewAspects = normaliseProductViewAspects(view);

                // Read rotation from either supported response field
                let rotationValue = view?.rotation;

                if (
                    rotationValue === null ||
                    rotationValue === undefined ||
                    rotationValue === ""
                ) {
                    rotationValue = view?.rotate;
                }

                // Convert the view rotation
                const rotation = Number(rotationValue);

                // Ignore malformed product views
                if (viewAspects.length === 0) {
                    return;
                }

                if (
                    !SUPPORTED_VIEW_ROTATIONS.includes(rotation)
                ) {
                    return;
                }

                // The configured role must permit this real view rotation
                if (
                    !roleViewSettings.rotations.includes(rotation)
                ) {
                    return;
                }

                // Store each aspect shared by the real view and configured role
                roleViewSettings.aspects.forEach((aspect) => {

                    // Skip aspects this real view cannot display
                    if (!viewAspects.includes(aspect)) { return; }

                    // Store this exact supported configuration
                    supportedConfigurationKeys.add(
                        aspect + "|" + rotation
                    );

                });

            });

        });

    });

    // Return the real view configurations available to this product
    return supportedConfigurationKeys;

};

// Get every mockup configuration supported by an active real product view
export const getAllMockupConfigurations = (
    canonicalProduct,
    configuredRoles = null
) => {

    // Read mockups in canonical product order
    const selectedMockups =
        getCanonicalProductMockups(canonicalProduct);

    // Applying configured roles also applies the real product-view restriction
    const restrictToSupportedProductViews =
        Array.isArray(configuredRoles);

    // Store the exact configurations supported by active product views
    let supportedProductViewConfigurationKeys =
        new Set();

    if (restrictToSupportedProductViews) {

        supportedProductViewConfigurationKeys =
            getSupportedProductViewConfigurationKeys(
                canonicalProduct,
                configuredRoles
            );

    }

    // Store exact mockup configurations in canonical order
    const configurations = [];

    // Prevent duplicate exact configurations
    const addedConfigurationKeys = new Set();

    selectedMockups.forEach((mockup) => {

        // Read the mockup id from either supported response shape
        const mockupId = Number(
            mockup?.id ||
            mockup?.cp_mockups_id
        );

        // Ignore malformed mockups
        if (!mockupId) {
            return;
        }

        // Read the aspect and rotation combinations supported by the mockup
        const mockupViewSettings =
            normaliseMockupViewSettings(
                mockup?.view_settings
            );

        mockupViewSettings.aspects.forEach((aspect) => {

            mockupViewSettings.rotations.forEach((rotation) => {

                // Build the aspect and rotation lookup used by real product views
                const productViewConfigurationKey =
                    aspect + "|" + rotation;

                // Do not offer configurations unsupported by a real active view
                if (
                    restrictToSupportedProductViews &&
                    !supportedProductViewConfigurationKeys.has(
                        productViewConfigurationKey
                    )
                ) {
                    return;
                }

                // Build the exact mockup configuration key
                const configurationKey =
                    getMockupConfigurationKey(
                        mockupId,
                        aspect,
                        rotation
                    );

                // Ignore invalid or duplicate exact configurations
                if (
                    !configurationKey ||
                    addedConfigurationKeys.has(
                        configurationKey
                    )
                ) {
                    return;
                }

                // Store this exact configuration
                addedConfigurationKeys.add(
                    configurationKey
                );

                configurations.push({
                    key: configurationKey,
                    mockup_id: mockupId,
                    aspect: aspect,
                    rotation: rotation,
                    mockup: mockup,
                });

            });

        });

    });

    // Return every mockup configuration which a real product view can display
    return configurations;

};

export const getAvailableMockupConfigurationsForConfiguredRoles = (canonicalProduct, configuredRoles) => {

    // Start with the supplied canonical product
    let customProduct = canonicalProduct;

    // Use the expanded custom product data when available
    if (canonicalProduct?.custom_product) {
        customProduct = canonicalProduct.custom_product;
    }

    // Read only the mockups selected for this canonical POD product
    const selectedMockups = getCanonicalProductMockups(canonicalProduct);

    // Read the actual product surfaces and views
    let surfaces = [];

    if (Array.isArray(customProduct?.surfaces)) {
        surfaces = customProduct.surfaces;
    }

    // Read the exact custom product code used by the mockup location summaries
    let customProductCode = "";

    if (customProduct?.product_code) {

        customProductCode = String(customProduct.product_code);

    } else if (customProduct?.code) {

        customProductCode = String(customProduct.code);

    }

    // Exact mockup locations cannot be resolved without the custom product code
    if (!customProductCode) {
        return [];
    }

    // Store the exact surface and print-area locations available to each selected mockup
    const mockupLocationKeysById = {};

    // Read the location summary now supplied on every mockup response
    selectedMockups.forEach((mockup) => {

        // Read the mockup id from either supported response shape
        const mockupId = Number(
            mockup?.id ||
            mockup?.cp_mockups_id
        );

        // Skip malformed mockup rows
        if (!mockupId) { return; }

        // Read the mockup locations safely
        let mockupLocations = [];

        if (Array.isArray(mockup?.locations)) {
            mockupLocations = mockup.locations;
        }

        // Keep only locations belonging to this exact custom product
        mockupLocations.forEach((location) => {

            // Multi-product mockups can contain locations for several custom products
            if (String(location?.product_code || "") !== customProductCode) {
                return;
            }

            // Read the exact product location
            const surfaceCode =
                String(location?.surface_code || "");

            const printAreaCode =
                String(location?.print_area_code || "");

            // Ignore incomplete location rows
            if (!surfaceCode || !printAreaCode) { return; }

            // Create the location set for this mockup when required
            if (!mockupLocationKeysById[mockupId]) {
                mockupLocationKeysById[mockupId] = new Set();
            }

            // Store the exact location once
            mockupLocationKeysById[mockupId].add(
                surfaceCode + "|" + printAreaCode
            );

        });

    });

    // Read configured roles safely
    let safeConfiguredRoles = [];

    if (Array.isArray(configuredRoles)) {
        safeConfiguredRoles = configuredRoles;
    }

    // Build a map of active configured roles
    const configuredRoleMap = {};

    safeConfiguredRoles.forEach((configuredRole) => {

        // Read the role code
        const roleCode = String(configuredRole?.role || "");

        // Skip missing roles
        if (!roleCode) { return; }

        // Skip inactive roles
        if (String(configuredRole?.active || "Y").toUpperCase() === "N") {
            return;
        }

        // Store the role's supported aspect and rotation settings
        configuredRoleMap[roleCode] =
            normaliseRoleViewSettings(configuredRole?.view_settings);

    });

    // Store exact configurations in canonical mockup order
    const configurations = [];

    // Prevent duplicate exact configurations
    const addedConfigurationKeys = new Set();

    // Check every mockup selected for this canonical POD product
    selectedMockups.forEach((mockup) => {

        // Read the mockup id
        const mockupId = Number(
            mockup?.id ||
            mockup?.cp_mockups_id
        );

        // Skip invalid mockups
        if (!mockupId) { return; }

        // Read the exact product locations carried by the canonical product response
        const mockupLocationKeys = mockupLocationKeysById[mockupId];

        // This mockup cannot represent this product without a matching location
        if (!mockupLocationKeys || mockupLocationKeys.size === 0) { return; }

        // Read the aspect and rotation combinations supported by this mockup
        const mockupViewSettings =
            normaliseMockupViewSettings(mockup?.view_settings);

        // Check every actual product surface
        surfaces.forEach((surface) => {

            // Read the surface code
            const surfaceCode = String(surface?.code || "");

            // Skip malformed surfaces
            if (!surfaceCode) { return; }

            // Read print areas safely
            let printAreas = [];

            if (Array.isArray(surface?.print_areas)) {
                printAreas = surface.print_areas;
            }

            // Check every print area on this surface
            printAreas.forEach((printArea) => {

                // Read the print-area code
                const printAreaCode = String(printArea?.code || "");

                // Skip malformed print areas
                if (!printAreaCode) { return; }

                // Build the exact product location
                const locationKey =
                    surfaceCode + "|" + printAreaCode;

                // The selected mockup must actually use this product location
                if (!mockupLocationKeys.has(locationKey)) {
                    return;
                }

                // The print area must have an active configured role
                const roleCode = String(printArea?.role || "");
                const roleViewSettings =
                    configuredRoleMap[roleCode] || null;

                // Skip print areas without an active configured role
                if (!roleViewSettings) { return; }

                // Read the real product views available on this print area
                let views = [];

                if (Array.isArray(printArea?.views)) {
                    views = printArea.views;
                }

                // Check every real product view
                views.forEach((view) => {

                    // Skip inactive views
                    if (
                        String(view?.active || "Y").toUpperCase() === "N"
                    ) {
                        return;
                    }

                    // Read every design aspect this real product view can display
                    const viewAspects = normaliseProductViewAspects(view);

                    // Rotation zero must not be treated as missing
                    if (
                        view?.rotation === null ||
                        view?.rotation === undefined ||
                        view?.rotation === ""
                    ) {
                        return;
                    }

                    // Read the exact view rotation
                    const rotation = Number(view.rotation);

                    // Skip invalid product-view data
                    if (viewAspects.length === 0) {
                        return;
                    }

                    if (!SUPPORTED_VIEW_ROTATIONS.includes(rotation)) {
                        return;
                    }

                    // The configured role and selected mockup must allow this real view rotation
                    if (!roleViewSettings.rotations.includes(rotation)) {
                        return;
                    }

                    if (!mockupViewSettings.rotations.includes(rotation)) {
                        return;
                    }

                    // Check configured-role aspects in preference order
                    roleViewSettings.aspects.forEach((aspect) => {

                        // The real product view must support this design aspect
                        if (!viewAspects.includes(aspect)) { return; }

                        // The selected mockup must support this design aspect
                        if (!mockupViewSettings.aspects.includes(aspect)) { return; }

                        // Build the exact configuration key
                        const configurationKey =
                            getMockupConfigurationKey(
                                mockupId,
                                aspect,
                                rotation
                            );

                        // Ignore invalid or duplicate configurations
                        if (
                            !configurationKey ||
                            addedConfigurationKeys.has(configurationKey)
                        ) {
                            return;
                        }

                        // Store the exact available configuration
                        addedConfigurationKeys.add(configurationKey);

                        configurations.push({
                            key: configurationKey,
                            mockup_id: mockupId,
                            aspect: aspect,
                            rotation: rotation,
                            mockup: mockup,
                            surface_code: surfaceCode,
                            print_area_code: printAreaCode,
                            role: roleCode,
                        });

                    });

                });

            });

        });

    });

    // Return every valid exact configuration
    return configurations;

};


// Randomly select a configured number of distinct logical mockups for each rotation
export const selectRandomMockupConfigurationsByRotation = (
    configurations,
    mockupCount = 3
) => {

    // Read valid exact configurations safely
    const safeConfigurations = Array.isArray(configurations)
        ? configurations
        : [];

    // Normalise the requested count
    const safeMockupCount = Number.parseInt(mockupCount, 10);

    // Zero or an invalid count means no automatic mockups
    if (!Number.isInteger(safeMockupCount) || safeMockupCount <= 0) {
        return [];
    }

    // Group distinct logical mockups independently for each rotation
    const mockupsByRotation = new Map();

    safeConfigurations.forEach((configuration) => {

        // Read the exact logical mockup and rotation
        const mockupId = String(configuration?.mockup_id || "");
        const rotation = Number(configuration?.rotation);

        // Skip malformed configurations
        if (!mockupId || !Number.isFinite(rotation)) { return; }

        // Create the rotation group once
        const rotationKey = String(rotation);

        if (!mockupsByRotation.has(rotationKey)) {
            mockupsByRotation.set(rotationKey, []);
        }

        // Add each logical mockup only once within this rotation
        const rotationMockupIds = mockupsByRotation.get(rotationKey);

        if (!rotationMockupIds.includes(mockupId)) {
            rotationMockupIds.push(mockupId);
        }

    });

    // Randomly choose logical mockups independently within each rotation
    const selectedMockupKeys = new Set();

    mockupsByRotation.forEach((rotationMockupIds, rotationKey) => {

        // Shuffle a copy so the source list remains untouched
        const shuffledMockupIds = [...rotationMockupIds];

        for (let index = shuffledMockupIds.length - 1; index > 0; index--) {

            // Pick another position from the remaining pool
            const randomIndex = Math.floor(
                Math.random() * (index + 1)
            );

            // Swap the two positions
            const currentValue = shuffledMockupIds[index];
            shuffledMockupIds[index] = shuffledMockupIds[randomIndex];
            shuffledMockupIds[randomIndex] = currentValue;

        }

        // Keep up to the configured number of logical mockups
        shuffledMockupIds
            .slice(0, safeMockupCount)
            .forEach((mockupId) => {

                selectedMockupKeys.add(
                    rotationKey + "|" + mockupId
                );

            });

    });

    // Build the exact selected configurations
    const selectedConfigurations =
        safeConfigurations.filter((configuration) => {

            // Build the same rotation + logical mockup identity
            const selectionKey =
                String(Number(configuration?.rotation)) +
                "|" +
                String(configuration?.mockup_id || "");

            return selectedMockupKeys.has(selectionKey);

        });

    // Shuffle the final selected rows as well so the first storefront image can change
    for (
        let index = selectedConfigurations.length - 1;
        index > 0;
        index--
    ) {

        // Pick another position from the remaining selected rows
        const randomIndex =
            Math.floor(
                Math.random() * (index + 1)
            );

        // Swap the selected rows
        const currentValue =
            selectedConfigurations[index];

        selectedConfigurations[index] =
            selectedConfigurations[randomIndex];

        selectedConfigurations[randomIndex] =
            currentValue;

    }

    // Return the randomly selected rows in random display order
    return selectedConfigurations;

};



// Get one preferred mockup configuration for each active configured role
export const getPreferredMockupConfigurationsForConfiguredRoles = (
    canonicalProduct,
    configuredRoles
) => {

    // Read active configured roles in their configured order
    const activeConfiguredRoles = [];

    if (Array.isArray(configuredRoles)) {

        configuredRoles.forEach((configuredRole) => {

            // Ignore inactive roles
            if (
                String(
                    configuredRole?.active || "Y"
                ).toUpperCase() === "N"
            ) {
                return;
            }

            // Ignore roles without a role code
            if (!String(configuredRole?.role || "")) {
                return;
            }

            // Store this active role
            activeConfiguredRoles.push(
                configuredRole
            );

        });

    }

    // Store the preferred configurations in configured-role order
    const preferredConfigurations = [];

    // Prevent the same multi-area configuration being added more than once
    const addedConfigurationKeys = new Set();

    activeConfiguredRoles.forEach((configuredRole) => {

        // Get configurations which genuinely contain this role location
        const roleConfigurations =
            getAvailableMockupConfigurationsForConfiguredRoles(
                canonicalProduct,
                [configuredRole]
            );

        // Prefer a mockup dedicated to this one product location
        let preferredConfiguration =
            roleConfigurations.find((configuration) => {

                return (
                    getMockupProductLocationCount(
                        canonicalProduct,
                        configuration.mockup
                    ) === 1
                );

            });

        // Otherwise use the first mockup which contains this role
        if (
            !preferredConfiguration &&
            roleConfigurations.length > 0
        ) {
            preferredConfiguration =
                roleConfigurations[0];
        }

        // Stop when no mockup can represent this role
        if (!preferredConfiguration) {
            return;
        }

        // A multi-area mockup may already represent another active role
        if (
            addedConfigurationKeys.has(
                preferredConfiguration.key
            )
        ) {
            return;
        }

        // Store this preferred exact configuration
        addedConfigurationKeys.add(
            preferredConfiguration.key
        );

        preferredConfigurations.push(
            preferredConfiguration
        );

    });

    // Return one preferred configuration per available active role
    return preferredConfigurations;

};



export const getFirstAvailableMockupConfigurationForConfiguredRoles = (
    canonicalProduct,
    configuredRoles
) => {

    // Get the preferred role-specific configurations
    const preferredConfigurations =
        getPreferredMockupConfigurationsForConfiguredRoles(
            canonicalProduct,
            configuredRoles
        );

    // Return the first configured role's preferred mockup
    if (preferredConfigurations.length > 0) {
        return preferredConfigurations[0];
    }

    // When no location matches, retain the existing all-mockups fallback
    const allConfigurations =
        getAllMockupConfigurations(
            canonicalProduct,
            configuredRoles
        );

    if (allConfigurations.length > 0) {
        return allConfigurations[0];
    }

    // No mockup configuration is available
    return null;

};


export const mockupIsCompatibleWithConfiguredRole = (mockup, configuredRole) => {

    // Disabled or missing configured roles cannot make a mockup compatible
    if (!configuredRole || String(configuredRole.active || "Y").trim().toUpperCase() === "N") {
        return false;
    }

    // Read the mockup view settings
    const mockupViewSettings = normaliseMockupViewSettings(mockup?.view_settings);

    // Read the configured role view settings
    const roleViewSettings = normaliseRoleViewSettings(configuredRole?.view_settings);

    // Check for at least one shared aspect
    const aspectMatches = mockupViewSettings.aspects.some((aspect) => roleViewSettings.aspects.includes(aspect));

    // Check for at least one shared rotation
    const rotationMatches = mockupViewSettings.rotations.some((rotation) => roleViewSettings.rotations.includes(rotation));

    // A mockup must overlap the same configured role on both dimensions
    return aspectMatches && rotationMatches;

};



export const getCompatibleMockupsForConfiguredRoles = (mockups, configuredRoles) => {

    // Normalise the supplied mockups
    let safeMockups = [];
    if (Array.isArray(mockups)) { safeMockups = mockups; }

    // Normalise the supplied configured roles
    let safeConfiguredRoles = [];
    if (Array.isArray(configuredRoles)) { safeConfiguredRoles = configuredRoles; }

    // Return only mockups which overlap at least one active configured role
    return safeMockups.filter((mockup) => {

        // Check each configured role independently so aspect and rotation cannot match different roles
        return safeConfiguredRoles.some((configuredRole) => mockupIsCompatibleWithConfiguredRole(mockup, configuredRole));

    });

};



export const getFirstCompatibleMockupForConfiguredRoles = (mockups, configuredRoles) => {

    // Get the compatible mockups in their existing canonical order
    const compatibleMockups = getCompatibleMockupsForConfiguredRoles(mockups, configuredRoles);

    // Return the first compatible mockup when one exists
    if (compatibleMockups.length > 0) {
        return compatibleMockups[0];
    }

    // Return null when the product has no compatible mockups
    return null;

};



export const removeIncompatibleMockupImages = (
    images,
    canonicalProduct,
    configuredRoles,
    matchingLocationsOnly = false
) => {

    // Normalise current image rows
    const safeImages = Array.isArray(images) ? images : [];

    // Start with every configuration supported by an active role and real product view
    let availableConfigurations =
        getAllMockupConfigurations(
            canonicalProduct,
            configuredRoles
        );

    // Destructive design-role changes keep only mockups using a remaining role location
    if (matchingLocationsOnly) {

        availableConfigurations =
            getAvailableMockupConfigurationsForConfiguredRoles(
                canonicalProduct,
                configuredRoles
            );

    }

    // Build the exact configuration lookup
    const configurationMap = {};

    availableConfigurations.forEach((configuration) => {

        // Store the exact mockup, aspect and rotation configuration
        configurationMap[configuration.key] = configuration;

    });

    // Store compatible rows and prevent duplicate exact mockup configurations
    const compatibleImages = [];
    const addedConfigurationKeys = new Set();

    safeImages.forEach((imageRow) => {

        // Skip invalid rows
        if (!imageRow || typeof imageRow !== "object") { return; }

        // Manual bucket images are not restricted by product view settings
        if (!imageRow.mockup_id) {
            if (imageRow.image_id) { compatibleImages.push(imageRow); }
            return;
        }

        // Read and validate the stored exact configuration
        const mockupId = String(imageRow.mockup_id);
        const aspect = String(imageRow?.aspect || "").trim().toUpperCase();
        const rotation = Number(imageRow?.rotation);

        // Invalid or incomplete mockup rows are not accepted
        if (
            !SUPPORTED_VIEW_ASPECTS.includes(aspect) ||
            !SUPPORTED_VIEW_ROTATIONS.includes(rotation)
        ) {
            return;
        }

        // Build the stored exact configuration key
        const configurationKey = getMockupConfigurationKey(
            mockupId,
            aspect,
            rotation
        );

        // Find the exact configuration still permitted by the product and roles
        const resolvedConfiguration = configurationMap[configurationKey] || null;

        // Remove invalid or duplicate exact configurations
        if (
            !resolvedConfiguration ||
            addedConfigurationKeys.has(resolvedConfiguration.key)
        ) {
            return;
        }

        // Record this exact configuration
        addedConfigurationKeys.add(resolvedConfiguration.key);

        // Keep the compatible exact row
        compatibleImages.push({
            ...imageRow,
            mockup_id: resolvedConfiguration.mockup_id,
            aspect: resolvedConfiguration.aspect,
            rotation: resolvedConfiguration.rotation,
        });

    });

    // Destructive role changes keep one preferred mockup for every remaining role
    if (matchingLocationsOnly) {

        // Get the preferred exact configuration for each active role
        const preferredConfigurations =
            getPreferredMockupConfigurationsForConfiguredRoles(
                canonicalProduct,
                configuredRoles
            );

        // Store the preferred rows in active-role order
        const preferredImages = [];

        // Store the preferred configuration keys
        const preferredConfigurationKeys =
            new Set();

        preferredConfigurations.forEach(
            (preferredConfiguration) => {

                // Find an existing selected row using this exact configuration
                const existingImage =
                    compatibleImages.find((imageRow) => {

                        // Manual bucket images cannot match a mockup configuration
                        if (!imageRow?.mockup_id) {
                            return false;
                        }

                        // Build the selected row's exact configuration key
                        const existingConfigurationKey =
                            getMockupConfigurationKey(
                                imageRow.mockup_id,
                                imageRow.aspect,
                                imageRow.rotation
                            );

                        // Check whether this is the preferred configuration
                        return (
                            existingConfigurationKey ===
                            preferredConfiguration.key
                        );

                    });

                // Retain the existing generated or pending row when available
                if (existingImage) {

                    preferredImages.push({
                        ...existingImage,
                        mockup_id:
                            preferredConfiguration.mockup_id,
                        aspect:
                            preferredConfiguration.aspect,
                        rotation:
                            preferredConfiguration.rotation,
                    });

                } else {

                    // Add a new pending row for this active print area
                    preferredImages.push({
                        image_id: null,
                        mockup_id:
                            preferredConfiguration.mockup_id,
                        aspect:
                            preferredConfiguration.aspect,
                        rotation:
                            preferredConfiguration.rotation,
                        image_url: null,
                        thumbnail_url: null,
                    });

                }

                // Record this preferred configuration
                preferredConfigurationKeys.add(
                    preferredConfiguration.key
                );

            }
        );

        // Retain manual images and any additional selected mockups afterwards
        const remainingImages =
            compatibleImages.filter((imageRow) => {

                // Always retain manual bucket images
                if (!imageRow?.mockup_id) {
                    return true;
                }

                // Build this selected row's exact configuration key
                const configurationKey =
                    getMockupConfigurationKey(
                        imageRow.mockup_id,
                        imageRow.aspect,
                        imageRow.rotation
                    );

                // Preferred rows have already been moved to the front
                return (
                    !preferredConfigurationKeys.has(
                        configurationKey
                    )
                );

            });

        // Replace the compatible list with preferred rows first
        compatibleImages.length = 0;

        compatibleImages.push(
            ...preferredImages,
            ...remainingImages
        );

    }

    // Ensure the configured product retains at least one exact mockup row
    const hasMockupImage = compatibleImages.some((imageRow) => Boolean(imageRow?.mockup_id));

    if (!hasMockupImage && availableConfigurations.length > 0) {

        // Choose the preferred role match, falling back to the first available mockup
        const firstConfiguration =
            getFirstAvailableMockupConfigurationForConfiguredRoles(
                canonicalProduct,
                configuredRoles
            );

        // Add it before any manual bucket images
        if (firstConfiguration) {

            compatibleImages.unshift({
                image_id: null,
                mockup_id: firstConfiguration.mockup_id,
                aspect: firstConfiguration.aspect,
                rotation: firstConfiguration.rotation,
                image_url: null,
                thumbnail_url: null,
            });

        }

    }

    // Rebuild ordering after removal, migration or automatic insertion
    return compatibleImages.map((imageRow, imageIndex) => ({
        ...imageRow,
        ordering: imageIndex + 1,
    }));

};
